import {Toast} from 'antd-mobile';
import {PickUpBase64ToFile, PICKED_UP_FAILED_KEY} from "../../../services/PickedUpDataService";
import {
  PickUpImageCompressionOptions,
  isPickUpNativeBridgeAvailable,
  isNativeCameraAvailable,
  PickUpNativePermissions, PickUpNativeCamera
} from "../components/PickUpPhotoNativeBridge";
import geolocationService, {GeoPosition} from "./PickUpGeolocationService";
import { useLocationStore } from "../../../store/locationStore";
import i18n from 'i18n';

export type PickUpPositionType =
  | 'signFor'
  | 'packageImg'
  | 'doorNumber'
  | 'warehousePhoto'
  | 'file1'
  | 'file2'
  | 'file3'
  | 'file4'
  | 'file5'
  | 'file6';
export type PickUpPhotoType = {url:string,objectKey:string};
export type PhotoUpdateCallback = (updateFn: (prev: Record<PickUpPositionType, PickUpPhotoType | undefined>) => Record<PickUpPositionType, PickUpPhotoType | undefined>) => void;

export const PICKUP_FILE_PHOTO_SLOTS: PickUpPositionType[] = ['file1', 'file2', 'file3', 'file4', 'file5', 'file6'];

interface CompressionOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  enableResize: boolean;
}

interface WatermarkOptions {
  includeLocation: boolean;
  includeTimestamp: boolean;
  fontSize: number;
  fontColor: string;
  backgroundColor: string;
  opacity: number;
  padding: number;
}

class PickUpPhotoService {
  /**
   * 检查相机权限并请求
   * @returns Promise<boolean>
   */
  async checkAndRequestCameraPermission(): Promise<boolean> {
    // 首先检查权限
    const hasPermission = await PickUpNativePermissions.check('camera');
    if (hasPermission) {
      return true;
    }

    // 如果没有权限，请求权限
    return await PickUpNativePermissions.request('camera');
  }
  
  /**
   * 检查存储权限并请求
   * @returns Promise<boolean>
   */
  async checkAndRequestStoragePermission(): Promise<boolean> {
    if (!isPickUpNativeBridgeAvailable()) {
      return true; // 网页环境下默认返回true
    }
    
    // 首先检查权限
    const hasPermission = await PickUpNativePermissions.check('storage');
    if (hasPermission) {
      return true;
    }
    
    // 如果没有权限，请求权限
    return await PickUpNativePermissions.request('storage');
  }

  /**
   * 添加水印到图片
   * @param file 原始图片文件
   * @param watermarkOptions 水印选项
   * @returns Promise<File> 添加水印后的文件
   */
  async addWatermarkToImage(file: File, watermarkOptions: WatermarkOptions = {
    includeLocation: true,
    includeTimestamp: true,
    fontSize: 14,
    fontColor: '#FFFFFF',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    opacity: 1,
    padding: 8
  }): Promise<File> {
    try {
      // 获取位置和时间信息
      let locationText = '';
      let timestampText = '';

      if (watermarkOptions.includeTimestamp) {
        const now = new Date();
        timestampText = now.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      }

      if (watermarkOptions.includeLocation) {
        try {
          // 首先尝试从 locationStore 获取当前位置
          const locationState = useLocationStore.getState();
          let position = locationState.position;
          
          // 如果没有位置信息或位置信息过旧（超过5分钟），尝试更新
          const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
          if (!position || !locationState.lastUpdatedAt || locationState.lastUpdatedAt < fiveMinutesAgo) {
            console.log('[PhotoService] 位置信息不存在或过旧，尝试获取新位置');
            try {
              // 尝试获取新位置，但设置较短的超时时间，避免影响拍照体验
              position = await geolocationService.getOneTimePosition();
            } catch (error) {
              console.warn('[PhotoService] 获取新位置失败，使用缓存位置或默认值:', error);
              // 如果获取失败但有缓存位置，使用缓存位置
              position = locationState.position;
            }
          }
          
          if (position) {
            locationText = `${position.latitude.toFixed(6)}, ${position.longitude.toFixed(6)}`;
            console.log('[PhotoService] 使用位置信息:', locationText);
          } else {
            locationText = '';
            console.warn('[PhotoService] 无可用位置信息');
          }
        } catch (error) {
          console.warn('Failed to get location for watermark:', error);
          locationText = '';
        }
      }

      // 准备水印文本
      const watermarkLines: string[] = [];
      if (timestampText) watermarkLines.push(timestampText);
      if (locationText) watermarkLines.push(locationText);

      if (watermarkLines.length === 0) {
        // 没有水印内容，直接返回原文件
        return file;
      }

      // 使用Promise包装图片处理逻辑
      return new Promise<File>((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          try {
            // 设置canvas尺寸为图片尺寸
            canvas.width = img.width;
            canvas.height = img.height;

            // 绘制原始图片
            ctx?.drawImage(img, 0, 0);

            if (!ctx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }

            // 设置字体样式
            ctx.font = `${watermarkOptions.fontSize}px Arial, sans-serif`;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';

            // 计算文本尺寸
            const textMetrics = watermarkLines.map(line => ctx.measureText(line));
            const maxTextWidth = Math.max(...textMetrics.map(tm => tm.width));
            const lineHeight = watermarkOptions.fontSize * 1.2;
            const totalTextHeight = watermarkLines.length * lineHeight;

            // 计算背景框尺寸和位置
            const bgWidth = maxTextWidth + watermarkOptions.padding * 2;
            const bgHeight = totalTextHeight + watermarkOptions.padding * 2;
            const bgX = canvas.width - bgWidth - 10; // 距离右边10px
            const bgY = canvas.height - bgHeight - 10; // 距离底部10px

            // 绘制背景框
            ctx.globalAlpha = watermarkOptions.opacity;
            ctx.fillStyle = watermarkOptions.backgroundColor;
            ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

            // 绘制文本
            ctx.fillStyle = watermarkOptions.fontColor;
            const textStartX = canvas.width - watermarkOptions.padding - 10;
            const textStartY = canvas.height - watermarkOptions.padding - 10;

            watermarkLines.forEach((line, index) => {
              const y = textStartY - (watermarkLines.length - 1 - index) * lineHeight;
              ctx.fillText(line, textStartX, y);
            });

            // 重置透明度
            ctx.globalAlpha = 1;

            // 转换为blob
            canvas.toBlob((blob) => {
              if (blob) {
                const watermarkedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                console.log(`[PhotoService] 添加水印完成`);
                resolve(watermarkedFile);
              } else {
                reject(new Error('添加水印失败'));
              }
            }, 'image/jpeg', 0.95);

          } catch (error) {
            console.error('[PhotoService] 添加水印错误:', error);
            reject(error);
          }
        };

        img.onerror = () => {
          reject(new Error('图片加载失败'));
        };

        // 读取文件
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target?.result as string;
        };
        reader.onerror = () => {
          reject(new Error('文件读取失败'));
        };
        reader.readAsDataURL(file);
      });

    } catch (error) {
      throw error;
    }
  }

  /**
   * 压缩图片（JavaScript实现，参考Android逻辑）
   * @param file 原始图片文件
   * @param options 压缩选项
   * @returns Promise<File> 压缩后的文件
   */
  async compressImage(file: File, options: CompressionOptions = {
    maxWidth: 1080,
    maxHeight: 1080,
    quality: 75,
    enableResize: true
  }): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          let { width, height } = img;

          // 记录压缩前的大小
          const originalSize = file.size;
          console.log(`[PhotoService] 压缩前图片大小: ${(originalSize / 1024).toFixed(1)} KB`);

          // 如果需要调整尺寸
          if (options.enableResize && (width > options.maxWidth || height > options.maxHeight)) {
            const widthRatio = options.maxWidth / width;
            const heightRatio = options.maxHeight / height;
            const scaleFactor = Math.min(widthRatio, heightRatio);

            width = Math.round(width * scaleFactor);
            height = Math.round(height * scaleFactor);

            console.log(`[PhotoService] 调整图片尺寸: ${img.width}x${img.height} -> ${width}x${height}`);
          }

          // 设置canvas尺寸
          canvas.width = width;
          canvas.height = height;

          // 绘制图片到canvas
          ctx?.drawImage(img, 0, 0, width, height);

          // 转换为blob
          canvas.toBlob((blob) => {
            if (blob) {
              // 创建新的File对象
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });

              // 记录压缩后的大小
              const compressedSize = compressedFile.size;
              console.log(`[PhotoService] 压缩后图片大小: ${(compressedSize / 1024).toFixed(1)} KB`);
              console.log(`[PhotoService] 压缩比例: ${((1 - compressedSize / originalSize) * 100).toFixed(1)}%`);

              resolve(compressedFile);
            } else {
              reject(new Error('图片压缩失败'));
            }
          }, 'image/jpeg', options.quality / 100);

        } catch (error) {
          console.error('[PhotoService] 图片压缩错误:', error);
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      // 读取文件
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => {
        reject(new Error('文件读取失败'));
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * 打开相册选择照片
   * @param compressionOptions 图片压缩选项
   * @param includeWatermark 是否包含水印（默认true）
   * @returns Promise<File>
   */
  async selectFromGallery(compressionOptions?: PickUpImageCompressionOptions, includeWatermark: boolean = false): Promise<File> {
    // 如果有原生桥接，使用原生
    if (isPickUpNativeBridgeAvailable()) {
      try {
        // 检查存储权限
        const hasPermission = await this.checkAndRequestStoragePermission();
        if (!hasPermission) {
          throw new Error(i18n.t('pages.pickUp.photo.storagePermissionDenied') as string);
        }
        
        const base64Data = await PickUpNativeCamera.openGallery(compressionOptions);
        let file = await PickUpBase64ToFile(base64Data, `photo_${Date.now()}.jpg`);
        
        // 如果需要添加水印
        if (includeWatermark) {
          try {
            file = await this.addWatermarkToImage(file);
            console.log('[PhotoService] 相册选择照片已添加水印');
          } catch (error) {
            console.warn('[PhotoService] 添加水印失败，使用原始图片:', error);
            // 水印失败时仍然返回原始图片
          }
        }
        
        return file;
      } catch (error) {
        console.error('Failed to use native gallery:', error);
        // 原生相册失败时，不尝试相机权限，直接使用文件选择器
      }
    }

    // 回退到网页文件选择器（不使用相机API）
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      let isResolved = false;
      // 设置超时机制，防止iOS WKWebView无响应
              const timeout = setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            reject(new Error(i18n.t('pages.pickUp.photo.fileSelectionTimeout') as string));
          }
        }, 30000); // 30秒超时

      const cleanup = () => {
        clearTimeout(timeout);
        input.removeEventListener('change', handleChange);
        input.removeEventListener('cancel', handleCancel);
        input.removeEventListener('error', handleError);
        // 清理input元素
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      };

      const handleChange = async (event: Event) => {
        if (isResolved) return;

        try {
          const files = (event.target as HTMLInputElement).files;
          if (files && files.length > 0) {
            isResolved = true;
            cleanup();

            const originalFile = files[0];

            // 检查是否为浏览器环境，应用JavaScript压缩
            // 原生App会走原生桥接，不会到这里
            const isBrowser = !isPickUpNativeBridgeAvailable();
            console.log('[PhotoService] 原生桥接可用:', isPickUpNativeBridgeAvailable());
            console.log('[PhotoService] 是否为浏览器环境:', isBrowser);

            if (isBrowser) {
              console.log('[PhotoService] 浏览器环境，应用JavaScript压缩');
              let processedFile = await this.compressImage(originalFile, {
                maxWidth: 1080,
                maxHeight: 1080,
                quality: 75,
                enableResize: true
              });
              
              // 如果需要添加水印
              if (includeWatermark) {
                try {
                  processedFile = await this.addWatermarkToImage(processedFile);
                  console.log('[PhotoService] 浏览器相册选择照片已添加水印');
                } catch (error) {
                  console.warn('[PhotoService] 添加水印失败，使用压缩后的图片:', error);
                  // 水印失败时使用压缩后的图片
                }
              }
              
              resolve(processedFile);
            } else {
              console.log('[PhotoService] 原生App环境，使用原始文件');
              let processedFile = originalFile;
              
              // 如果需要添加水印
              if (includeWatermark) {
                try {
                  processedFile = await this.addWatermarkToImage(originalFile);
                  console.log('[PhotoService] 原生App相册选择照片已添加水印');
                } catch (error) {
                  console.warn('[PhotoService] 添加水印失败，使用原始图片:', error);
                  // 水印失败时使用原始图片
                }
              }
              
              resolve(processedFile);
            }
          } else {
            isResolved = true;
            cleanup();
            reject(new Error(i18n.t('pages.pickUp.photo.noFileSelected') as string));
          }
        } catch (error) {
          isResolved = true;
          cleanup();
          reject(error);
        }
      };

      const handleCancel = () => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        reject(new Error(i18n.t('pages.pickUp.photo.selectionCancelled') as string));
      };

      const handleError = (error: Event) => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        reject(new Error(`File selection error: ${error}`));
      };

      // 使用addEventListener而不是onchange，更可靠
      input.addEventListener('change', handleChange);
      input.addEventListener('cancel', handleCancel);
      input.addEventListener('error', handleError);

      // 添加input到DOM中，提高iOS兼容性
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      input.style.top = '-9999px';
      document.body.appendChild(input);

      try {
        input.click();
      } catch (error) {
        isResolved = true;
        cleanup();
        reject(new Error(`Failed to trigger file input: ${error}`));
      }
    });
  }

  /**
   * 打开相机拍照
   * @param useFrontCamera 是否使用前置相机
   * @param compressionOptions 图片压缩选项
   * @param includeWatermark 是否包含水印（默认true）
   * @returns Promise<File>
   */
  async takePhoto(useFrontCamera: boolean = false, compressionOptions?: PickUpImageCompressionOptions, includeWatermark: boolean = false): Promise<File> {
    // Android：SwiftXBridge 原生相机
    if (isPickUpNativeBridgeAvailable()) {
      try {
        // 检查相机权限
        const cameraPermission = await this.checkAndRequestCameraPermission();
        if (!cameraPermission) {
          throw new Error(i18n.t('pages.pickUp.photo.cameraPermissionDenied') as string);
        }

        const base64Data = await PickUpNativeCamera.openCamera(useFrontCamera, compressionOptions);
        let file = await PickUpBase64ToFile(base64Data, `camera_${Date.now()}.jpg`);
        
        // 如果需要添加水印
        if (includeWatermark) {
          try {
            file = await this.addWatermarkToImage(file);
            console.log('[PhotoService] 相机拍照已添加水印');
          } catch (error) {
            console.warn('[PhotoService] 添加水印失败，使用原始图片:', error);
            // 水印失败时仍然返回原始图片
          }
        }
        
        return file;
      } catch (error) {
        console.error('Failed to use native camera:', error);
        throw error;
      }
    }

    // iOS：没有 SwiftXBridge，用 <input type=file capture> 强制拉起系统相机，跳过相册/拍照选择页
    if (isNativeCameraAvailable()) {
      try {
        let file = await this.openCaptureFilePicker(useFrontCamera ? 'user' : 'environment');

        if (includeWatermark) {
          try {
            file = await this.addWatermarkToImage(file);
            console.log('[PhotoService] iOS 相机拍照已添加水印');
          } catch (error) {
            console.warn('[PhotoService] 添加水印失败，使用原始图片:', error);
          }
        }

        return file;
      } catch (error) {
        console.error('Failed to use iOS capture camera:', error);
        throw error;
      }
    }

    // 如果没有原生桥接，抛出错误（WebView环境下不支持直接相机访问）
    throw new Error(i18n.t('pages.pickUp.photo.nativeIntegrationRequired') as string);
  }

  /**
   * iOS 强制拍照：<input type=file accept=image/* capture> 的 Promise 化封装。
   *
   * capture 属性会让系统跳过"相册/拍照"选择页，直接进入系统相机，且仍是标准 <input>，
   * 取消时 WebKit 的 cancel 事件依旧可靠触发（不同于自研原生相机页需要额外接一条取消回调）。
   * 走到本方法时 isPickUpNativeBridgeAvailable() 恒为 false（Android 已在上面分支返回），
   * 因此压缩按浏览器规格统一处理，不需要 selectFromGallery 里那套"原生相册失败时跳过压缩"的判断。
   */
  private openCaptureFilePicker(capture: 'environment' | 'user'): Promise<File> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.setAttribute('capture', capture);

      let isResolved = false;
      // 设置超时机制，防止iOS WKWebView无响应
      const timeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          reject(new Error(i18n.t('pages.pickUp.photo.fileSelectionTimeout') as string));
        }
      }, 30000); // 30秒超时

      const cleanup = () => {
        clearTimeout(timeout);
        input.removeEventListener('change', handleChange);
        input.removeEventListener('cancel', handleCancel);
        input.removeEventListener('error', handleError);
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      };

      const handleChange = async (event: Event) => {
        if (isResolved) return;

        try {
          const files = (event.target as HTMLInputElement).files;
          if (files && files.length > 0) {
            isResolved = true;
            cleanup();

            const processedFile = await this.compressImage(files[0], {
              maxWidth: 1080,
              maxHeight: 1080,
              quality: 75,
              enableResize: true
            });
            resolve(processedFile);
          } else {
            isResolved = true;
            cleanup();
            reject(new Error(i18n.t('pages.pickUp.photo.noFileSelected') as string));
          }
        } catch (error) {
          isResolved = true;
          cleanup();
          reject(error as Error);
        }
      };

      const handleCancel = () => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        reject(new Error(i18n.t('pages.pickUp.photo.selectionCancelled') as string));
      };

      const handleError = (error: Event) => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        reject(new Error(`File selection error: ${error}`));
      };

      input.addEventListener('change', handleChange);
      input.addEventListener('cancel', handleCancel);
      input.addEventListener('error', handleError);

      input.style.position = 'absolute';
      input.style.left = '-9999px';
      input.style.top = '-9999px';
      document.body.appendChild(input);

      try {
        input.click();
      } catch (error) {
        isResolved = true;
        cleanup();
        reject(new Error(`Failed to trigger file input: ${error}`));
      }
    });
  }

  /**
   * 将File对象转换为Base64字符串
   * @param file 文件对象
   * @returns Promise<string> Base64字符串
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  async handlePhotoNotUpload(
      type: PickUpPositionType,
      file: File,
      onPhotosUpdate: PhotoUpdateCallback,
  ): Promise<void> {
    try {

      // 使用提取的方法将文件转换为base64格式
      const base64Data = await this.fileToBase64(file);

      onPhotosUpdate((prev) => ({
        ...prev,
        [type]: { url: base64Data, objectKey: PICKED_UP_FAILED_KEY}
      }));

    } catch (error) {
      console.error('Failed handle photo not upload:', error);
      Toast.show({ content: i18n.t('pages.pickUp.photo.uploadFailed'), position: 'bottom' });
      throw error;
    }
  }

  /**
   * 处理删除照片
   * @param type 照片类型
   * @param onPhotosUpdate 照片更新回调
   * @returns Promise<void>
   */
  async handleDeletePhoto(
    type: PickUpPositionType,
    onPhotosUpdate: PhotoUpdateCallback
  ): Promise<void> {
    onPhotosUpdate((prev) => {
      const newState = { ...prev };
      delete newState[type];
      return newState;
    });
    Toast.show({content: i18n.t('pages.pickUp.photo.deleted'), position: 'bottom' });
  }
}

const photoService = new PickUpPhotoService();
export default photoService; 
